import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo
} from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Modal
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import { useOrdinalColorScale } from '@nivo/colors';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import {
  formatStat,
  ProductInfoResponse,
  RetailerContext,
  useSpot
} from 'framework';
import { ProductInfoSummary } from '../../components';

const ChartContainer = styled(Box)`
  height: 300px;
  width: 100%;
  margin-top: 16px;
`;

const SimpleValueDisplay = styled(Typography)`
  font-size: 3rem;
  font-weight: 700;
  text-align: center;
  color: #2c3e50;
  margin: 20px 0;
`;

const formatAxisValue = (val: string | number) => {
  if (
    moment.isDate(val) ||
    (typeof val === 'string' && moment(val).isValid())
  ) {
    return moment(val).format('D MMM');
  }

  if (
    typeof val === 'number' ||
    (typeof val === 'string' && !isNaN(Number(val)))
  ) {
    const num = typeof val === 'number' ? val : Number(val);

    const decimals = Math.abs(num) < 10 ? 2 : 0;

    return formatStat(num, false, decimals);
  }

  return String(val);
};

export const BarChart: React.FC<{
  data: Array<{ label: string; value: number }>;
}> = ({ data }) => {
  const chartData = data.map(item => ({
    id: Object.values(item)[0],
    value: Object.values(item)[1]
  }));

  const { t } = useTranslation();

  const colors = useOrdinalColorScale(
    {
      scheme: 'set2',
      size: data.length
    },
    'id'
  );

  return (
    <ChartContainer>
      <ResponsiveBar
        data={chartData}
        keys={['value']}
        indexBy="id"
        margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
        padding={0.3}
        colors={bar => colors(bar.indexValue.toString())}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
          legendPosition: 'middle',
          legendOffset: 50,
          format: value => formatAxisValue(value)
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: 'middle',
          legendOffset: -50,
          format: value => formatAxisValue(value)
        }}
        tooltipLabel={val =>
          `${t('label')}: ${val.data.id as string}
${t('value')}`
        }
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        animate={true}
      />
    </ChartContainer>
  );
};

export const PieChart: React.FC<{
  data: Array<{ label: string; value: number }>;
}> = ({ data }) => {
  // Transform data for Nivo Pie Chart
  const chartData = data.map(item => ({
    id: Object.values(item)[0],
    label: Object.values(item)[0],
    value: Object.values(item)[1]
  }));

  return (
    <ChartContainer>
      <ResponsivePie
        data={chartData}
        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
        innerRadius={0.5}
        padAngle={0.7}
        cornerRadius={3}
        colors={{ scheme: 'set2' }}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
        arcLabelsSkipAngle={10}
        arcLabelsTextColor="#333333"
        animate={true}
        legends={[
          {
            anchor: 'bottom',
            direction: 'row',
            translateY: 56,
            itemWidth: 100,
            itemHeight: 18,
            itemTextColor: '#999',
            symbolSize: 18,
            symbolShape: 'circle'
          }
        ]}
      />
    </ChartContainer>
  );
};

export const LineChart: React.FC<{
  data: { [series: string]: Array<{ x: string | number; y: number }> };
}> = ({ data }) => {
  // Transform data for Nivo Line Chart
  const chartData = Object.entries(data).map(([key, values]) => ({
    id: key,
    data: values.map(point => ({
      x: Object.values(point)[0],
      y: Object.values(point)[1]
    }))
  }));

  return (
    <ChartContainer>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 20, right: 20, bottom: 40, left: 50 }}
        lineWidth={3}
        enablePoints
        isInteractive
        enableSlices="x"
        enableCrosshair
        crosshairType="cross"
        curve="monotoneX"
        pointSize={10}
        pointColor="#ffffff"
        pointBorderWidth={3}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        xScale={{
          type: 'point'
          // If the x values are dates, you might want to use:
          // type: 'time', format: '%Y-%m-%d', precision: 'day'
        }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
          legendOffset: 36,
          legendPosition: 'middle',
          format: value => formatAxisValue(value)
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendOffset: -40,
          legendPosition: 'middle',
          format: value => formatAxisValue(value)
        }}
        colors={{ scheme: 'category10' }}
        pointLabel="y"
        legends={[]}
      />
    </ChartContainer>
  );
};

export const DataTable: React.FC<{ data: Array<{ [key: string]: any }> }> = ({
  data
}) => {
  if (!data.length) return null;

  const columns = Object.keys(data[0]);

  return (
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column}
                align="center"
                sx={{ fontWeight: 'bold' }}
              >
                {column
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/^./, str => str.toUpperCase())}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map(column => (
                <TableCell key={`${rowIndex}-${column}`} align="center">
                  {typeof row[column] === 'number'
                    ? formatAxisValue(row[column])
                    : row[column]?.toString()}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const NumberDisplay: React.FC<{ value: number | string }> = ({
  value
}) => {
  const formattedValue =
    typeof value === 'number'
      ? formatAxisValue(value)
      : typeof value === 'string' && !isNaN(Number(value))
        ? formatAxisValue(Number(value))
        : String(value);

  return <SimpleValueDisplay>{formattedValue}</SimpleValueDisplay>;
};

const TextDisplayWrapper = styled.div`
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  white-space: pre-wrap;
  overflow-x: auto;
  font-family: 'Roboto Mono', monospace;
`;

export const TextDisplay: React.FC<{ text: string }> = ({ text }) => {
  return <TextDisplayWrapper>{text}</TextDisplayWrapper>;
};

const ProductCard = styled(Card)`
  position: relative;
  transition: all 0.2s ease-in-out;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: contain;
  padding: 8px;
  background-color: #f9f9f9;
`;

const ProductTitle = styled(Typography)`
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 48px;
`;

const ValueBadge = styled(Chip)`
  position: absolute;
  top: 8px;
  right: 8px;
  font-weight: bold;
  background-color: rgba(25, 118, 210, 0.9);
  color: white;
`;

const ModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 24px;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

export const ProductListVisualization: React.FC<{
  data: Array<{ productId: string; value: number }>;
}> = ({ data }) => {
  const { t } = useTranslation();
  const { query, spot } = useSpot();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<{
    [id: string]: ProductInfoResponse;
  }>({});
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  const { retailer } = useContext(RetailerContext);

  // Extract all product IDs from data
  const productIds = useMemo(() => data.map(item => item.productId), [data]);

  // Fetch product information for each ID
  const fetchProductInfo = useCallback(async () => {
    if (!retailer) return;

    setLoading(true);

    try {
      const uniqueProductIds = Array.from(new Set(productIds));

      for (const productId of uniqueProductIds) {
        try {
          await query(
            `product-info/${encodeURIComponent(productId)}`,
            { retailer: retailer.slug },
            ['productInfo', retailer.slug, productId]
          );
        } catch (e) {
          console.error(`Error fetching product info for ${productId}:`, e);
        }
      }

      // Collect all the products that were successfully fetched
      const productResults: { [id: string]: ProductInfoResponse } = {};

      for (const productId of uniqueProductIds) {
        const productInfo =
          spot.data?.productInfo?.[retailer.slug]?.[productId];
        if (productInfo) {
          productResults[productId] = productInfo;
        }
      }

      setProducts(productResults);
    } catch (err) {
      console.error('Error fetching product information:', err);
    } finally {
      setLoading(false);
    }
  }, [productIds, retailer, query, spot]);

  useEffect(() => {
    fetchProductInfo();
  }, [fetchProductInfo]);

  const handleProductClick = (productId: string) => {
    setSelectedProductId(productId);
  };

  const handleModalClose = () => {
    setSelectedProductId(null);
  };

  const selectedProduct = selectedProductId
    ? products[selectedProductId]
    : null;

  // Create a map of product values from data
  const productValues: { [id: string]: number } = {};
  data.forEach(item => {
    productValues[item.productId] = item.value;
  });

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {Object.keys(products)
          .sort((a, b) => {
            const aValue = productValues[a] || 0;
            const bValue = productValues[b] || 0;
            return bValue - aValue;
          })
          .map(productId => {
            const product = products[productId];
            const value = productValues[productId];

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={productId}>
                <ProductCard>
                  <CardActionArea onClick={() => handleProductClick(productId)}>
                    <ProductImage
                      src={
                        product?.imageLink ||
                        'https://via.placeholder.com/150?text=No+Image'
                      }
                      alt={product?.name || productId}
                      onError={e => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src =
                          'https://via.placeholder.com/150?text=No+Image';
                      }}
                    />
                    {typeof value === 'number' && (
                      <ValueBadge label={formatAxisValue(value)} />
                    )}
                    <CardContent>
                      <ProductTitle variant="subtitle1">
                        {product?.name || productId}
                      </ProductTitle>
                      <Typography variant="body2" color="text.secondary">
                        {product?.brand || t('unknownBrand')}
                      </Typography>
                      {product?.gender && (
                        <Typography variant="body2" color="text.secondary">
                          {t(product.gender.toLowerCase())}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </ProductCard>
              </Grid>
            );
          })}
      </Grid>

      <Modal
        open={!!selectedProductId}
        onClose={handleModalClose}
        aria-labelledby="product-detail-modal"
      >
        <ModalContent>
          {selectedProduct ? (
            <ProductInfoSummary
              product={selectedProduct}
              onRefresh={() => fetchProductInfo()}
              onUpdateProduct={updatedProduct => {
                if (selectedProductId) {
                  setProducts({
                    ...products,
                    [selectedProductId]: updatedProduct as ProductInfoResponse
                  });
                }
              }}
            />
          ) : (
            <Typography>{t('productNotFound')}</Typography>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
